import type { CountSpecMisc } from "../../Processing/types/builderMisc";
import type {
  Table2DComponentSpec,
  Table2DDimension,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";
import { table2dPinnedAsRows } from "../../WidgetComponents/table2dGrid";

// ── Dimension grouping & validation ──

type DimGroup = "entity" | "time" | "metric";

export function dimGroup(d: Table2DDimension): DimGroup {
  if (d === "metric") return "metric";
  if (d === "day" || d === "week" || d === "month" || d === "weekday") return "time";
  return "entity";
}

// Same-group pairs the backend supports explicitly (schema.py
// _TABLE_2D_SAME_GROUP_PAIRS): drill as a secondary axis under player or
// position, and week × weekday as a calendar-style grid.
const SAME_GROUP_PAIRS = new Set(["player|drill", "position|drill", "week|weekday"]);

function isAllowedSameGroupPair(a: Table2DDimension, b: Table2DDimension): boolean {
  return SAME_GROUP_PAIRS.has(`${a}|${b}`) || SAME_GROUP_PAIRS.has(`${b}|${a}`);
}

/** Returns true when the row/col combination is meaningful. */
export function isValidDimPair(row: Table2DDimension, col: Table2DDimension): boolean {
  if (isAllowedSameGroupPair(row, col)) return true;
  if (row === "weekday" || col === "weekday") return false; // weekday pairs only with week
  return dimGroup(row) !== dimGroup(col);
}

/** When `changed` makes `other` invalid, returns a sensible replacement for `other`. */
export function fixOtherDim(changed: Table2DDimension, other: Table2DDimension): Table2DDimension {
  if (isValidDimPair(changed, other)) return other;
  if (changed === "weekday") return "week"; // weekday's only partner
  const g = dimGroup(changed);
  if (g === "entity") return "day";   // entity → default col is a time grain
  if (g === "time")   return "player"; // time → default col is an entity
  return "day";                        // metric → default col is a time grain
}

export const ALL_DIM_OPTIONS: { value: Table2DDimension; label: string }[] = [
  { value: "player",   label: "Player"   },
  { value: "position", label: "Position" },
  { value: "drill",    label: "Drill"    },
  { value: "metric",   label: "Metric"   },
  { value: "day",      label: "Day"      },
  { value: "week",     label: "Week"     },
  { value: "weekday",  label: "Weekday"  },
  { value: "month",    label: "Month"    },
];

/**
 * Mark Drill unusable while any metric is a count.
 *
 * None of the counted tables carries a drill relation, so the backend rejects a drill
 * group-by on a count outright — every drill would otherwise report the same total.
 * Disabled rather than removed, so the option does not silently vanish and reappear as
 * the author changes metric.
 */
export function withCountDimRules(
  options: { value: Table2DDimension; label: string }[],
  hasCount: boolean,
) {
  if (!hasCount) return options;
  return options.map((o) =>
    o.value === "drill"
      ? { ...o, disabled: true, title: "A count cannot be split by drill" }
      : o,
  );
}

export function validRowOptions(colDim: Table2DDimension) {
  return ALL_DIM_OPTIONS.filter((o) => isValidDimPair(o.value, colDim));
}

export function validColOptions(rowDim: Table2DDimension) {
  return ALL_DIM_OPTIONS.filter((o) => isValidDimPair(rowDim, o.value));
}

/**
 * Pinned Best/Avg entries make sense for every non-metric pair: entity × time,
 * player/position × drill, and week × weekday.
 */
export function isPinnedRelevant(row: Table2DDimension, col: Table2DDimension): boolean {
  return row !== "metric" && col !== "metric" && isValidDimPair(row, col);
}

/**
 * When true, Best/Avg should be rendered as extra rows; when false, extra
 * columns. Delegates to the shared primary-axis rule in table2dGrid.
 */
export function pinnedAreRows(row: Table2DDimension, col: Table2DDimension): boolean {
  return table2dPinnedAsRows(row, col);
}
import type { DateRangeFormFields } from "../shared/DateRangeForm";
import type { PlayersFormFields } from "../shared/PlayersForm";
import { DATE_RANGE_DEFAULTS } from "../shared/DateRangeForm";
import { PLAYERS_DEFAULTS, resolveSubjectNames } from "../shared/PlayersForm";

// ── State ──

export interface Table2DMetricEntry {
  id: string;       // React list key
  queryId: string;  // backend QuerySpec ID
  metricId: string | null;
  metricAgg: "avg" | "sum" | "best";
  /** Which items a count metric tallies. Absent for every measured metric. */
  countSpec?: CountSpecMisc;
}

export interface Table2DFormState extends DateRangeFormFields, PlayersFormFields {
  title: string;
  rowDimension: Table2DDimension;
  colDimension: Table2DDimension;
  /**
   * When neither dimension is "metric": one entry (the cell value metric).
   * When one dimension is "metric": N entries — one per row/col header.
   */
  metrics: Table2DMetricEntry[];
  colorize: boolean;
  showDataLabels: boolean;
  showBestCol: boolean;
  showAvgCol: boolean;
  showTotalCol: boolean;
  excludeInjuredPlayers: boolean;
  expandedKey: string | string[];
}

// ── Actions ──

export type Table2DAction =
  | { type: "SET_TITLE"; title: string }
  | { type: "SET_ROW_DIMENSION"; value: Table2DDimension }
  | { type: "SET_COL_DIMENSION"; value: Table2DDimension }
  | { type: "SWAP_DIMENSIONS" }
  | { type: "ADD_METRIC" }
  | { type: "REMOVE_METRIC"; index: number }
  | { type: "UPDATE_METRIC"; index: number; patch: Partial<Omit<Table2DMetricEntry, "id">> }
  | { type: "SET_COLORIZE"; value: boolean }
  | { type: "SET_SHOW_DATA_LABELS"; value: boolean }
  | { type: "SET_SHOW_BEST_COL"; value: boolean }
  | { type: "SET_SHOW_AVG_COL"; value: boolean }
  | { type: "SET_SHOW_TOTAL_COL"; value: boolean }
  | { type: "SET_EXCLUDE_INJURED_PLAYERS"; value: boolean }
  | { type: "SET_EXPANDED_KEY"; key: string | string[] }
  | { type: "UPDATE_DATE_RANGE"; patch: Partial<DateRangeFormFields> }
  | { type: "UPDATE_PLAYERS"; patch: Partial<PlayersFormFields> };

// ── Helpers ──

function createMetricEntry(): Table2DMetricEntry {
  return { id: crypto.randomUUID(), queryId: crypto.randomUUID(), metricId: null, metricAgg: "avg" };
}

// ── Reducer ──

export function table2dReducer(state: Table2DFormState, action: Table2DAction): Table2DFormState {
  switch (action.type) {
    case "SET_TITLE":
      return { ...state, title: action.title };

    case "SET_ROW_DIMENSION": {
      const newRow = action.value;
      const newCol = fixOtherDim(newRow, state.colDimension);
      return {
        ...state,
        rowDimension: newRow,
        colDimension: newCol,
        ...(!isPinnedRelevant(newRow, newCol) && { showBestCol: false, showAvgCol: false, showTotalCol: false }),
      };
    }

    case "SET_COL_DIMENSION": {
      const newCol = action.value;
      const newRow = fixOtherDim(newCol, state.rowDimension);
      return {
        ...state,
        colDimension: newCol,
        rowDimension: newRow,
        ...(!isPinnedRelevant(newRow, newCol) && { showBestCol: false, showAvgCol: false, showTotalCol: false }),
      };
    }

    case "SWAP_DIMENSIONS": {
      const newRow = state.colDimension;
      const newCol = state.rowDimension;
      return {
        ...state,
        rowDimension: newRow,
        colDimension: newCol,
        ...(!isPinnedRelevant(newRow, newCol) && { showBestCol: false, showAvgCol: false, showTotalCol: false }),
      };
    }

    case "ADD_METRIC":
      return { ...state, metrics: [...state.metrics, createMetricEntry()] };

    case "REMOVE_METRIC":
      if (state.metrics.length <= 1) return state;
      return { ...state, metrics: state.metrics.filter((_, i) => i !== action.index) };

    case "UPDATE_METRIC": {
      // Disabling the Drill option does not retract a dimension already set to it, and
      // the backend rejects a drill group-by on a count. Fall back to Player, which is
      // valid opposite every other dimension.
      const becameACount =
        action.patch.countSpec != null && state.metrics[action.index]?.countSpec == null;
      const rowDimension =
        becameACount && state.rowDimension === "drill" ? "player" : state.rowDimension;
      const colDimension =
        becameACount && state.colDimension === "drill" ? "player" : state.colDimension;
      return {
        ...state,
        rowDimension,
        colDimension,
        // A drill subject is silently dropped for a count; clear it with the dimension.
        selectedDrills: becameACount ? [] : state.selectedDrills,
        metrics: state.metrics.map((m, i) =>
          i === action.index ? { ...m, ...action.patch } : m,
        ),
      };
    }

    case "SET_COLORIZE":
      return { ...state, colorize: action.value };

    case "SET_SHOW_DATA_LABELS":
      return { ...state, showDataLabels: action.value };

    case "SET_SHOW_BEST_COL":
      return { ...state, showBestCol: action.value };

    case "SET_SHOW_AVG_COL":
      return { ...state, showAvgCol: action.value };

    case "SET_SHOW_TOTAL_COL":
      return { ...state, showTotalCol: action.value };

    case "SET_EXCLUDE_INJURED_PLAYERS":
      return { ...state, excludeInjuredPlayers: action.value };

    case "SET_EXPANDED_KEY":
      return { ...state, expandedKey: action.key };

    case "UPDATE_DATE_RANGE":
      return { ...state, ...action.patch };

    case "UPDATE_PLAYERS":
      return { ...state, ...action.patch };

    default:
      return state;
  }
}

// ── Factories ──

export function createInitialState(): Table2DFormState {
  return {
    title: "",
    rowDimension: "player",
    colDimension: "day",
    metrics: [createMetricEntry()],
    colorize: false,
    showDataLabels: true,
    showBestCol: true,
    showAvgCol: true,
    showTotalCol: false,
    excludeInjuredPlayers: true,
    expandedKey: ["__date_range"],
    ...DATE_RANGE_DEFAULTS,
    ...PLAYERS_DEFAULTS,
  };
}

export function hydrateFromSpec(spec: Table2DComponentSpec): Table2DFormState {
  const misc: import("../../Processing/types").Table2DBuilderMisc = spec.table_2d_builder_misc ?? {};
  const miscMetrics = misc.metrics ?? [];

  const metrics: Table2DMetricEntry[] =
    spec.queryIds.length > 0
      ? spec.queryIds.map((qid, i) => ({
          id: crypto.randomUUID(),
          queryId: qid,
          metricId: miscMetrics[i]?.metricId ?? null,
          metricAgg: (miscMetrics[i]?.metricAgg as "avg" | "sum" | "best") ?? "avg",
          countSpec: misc.countSpecs?.[qid],
        }))
      : [createMetricEntry()];

  return {
    title: spec.display.title ?? "",
    rowDimension: spec.rowDimension,
    colDimension: spec.colDimension,
    metrics,
    colorize: spec.colorize,
    showDataLabels: spec.showDataLabels ?? true,
    showBestCol: spec.pinnedCols.best,
    showAvgCol: spec.pinnedCols.avg,
    showTotalCol: spec.pinnedCols.total ?? false,
    excludeInjuredPlayers: misc.excludeInjuredPlayers ?? true,
    expandedKey: ["__date_range"],
    dateRange: misc.dateRange ?? DATE_RANGE_DEFAULTS.dateRange,
    showAdvancedDate: misc.showAdvancedDate ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
    startDateType: misc.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
    startDateLatestN: misc.startDateLatestN,
    startDateAbsolute: misc.startDateAbsolute ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
    startDateOffset: misc.startDateOffset ?? DATE_RANGE_DEFAULTS.startDateOffset,
    endDateType: misc.endDateType ?? DATE_RANGE_DEFAULTS.endDateType,
    endDateAbsolute: misc.endDateAbsolute ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
    endDateOffset: misc.endDateOffset ?? DATE_RANGE_DEFAULTS.endDateOffset,
    dayFilter: misc.dayFilter ?? DATE_RANGE_DEFAULTS.dayFilter,
    dateControllerId: misc.dateControllerId,
    subjectControllerId: misc.subjectControllerId,
    subjectControllerMode: misc.subjectControllerMode,
    selectedSubjects: misc.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects,
    selectedDrills: misc.selectedDrills ?? PLAYERS_DEFAULTS.selectedDrills,
    subjectActiveTab: misc.subjectActiveTab ?? PLAYERS_DEFAULTS.subjectActiveTab,
  };
}

export function formStateToSpec(
  state: Table2DFormState,
  _metrics: MetricOption[],
  getSubjectEntities?: (t: string) => SubjectEntity[],
): Table2DComponentSpec | null {
  const selectedSubjectNames = getSubjectEntities
    ? resolveSubjectNames(state.selectedSubjects, state.selectedDrills, getSubjectEntities)
    : [];

  if (!isValidDimPair(state.rowDimension, state.colDimension)) return null;

  const configuredMetrics = state.metrics.filter((m) => m.metricId != null);
  if (configuredMetrics.length === 0) return null;

  // Only emit configured metrics. queryIds, misc.metrics, and buildQueriesForTable2D
  // are paired by index, so an unconfigured row would leave a queryId with no matching
  // query (backend can't resolve it → whole widget blanks). entity×time uses a single
  // cell-value metric and the backend requires exactly one queryId.
  const bothNonMetric = state.rowDimension !== "metric" && state.colDimension !== "metric";
  const effectiveMetrics = bothNonMetric ? configuredMetrics.slice(0, 1) : configuredMetrics;

  const pinnedRelevant = isPinnedRelevant(state.rowDimension, state.colDimension);

  return {
    type: "table_2d",
    display: { title: state.title || "" },
    rowDimension: state.rowDimension,
    colDimension: state.colDimension,
    queryIds: effectiveMetrics.map((m) => m.queryId),
    colorize: state.colorize,
    showDataLabels: state.showDataLabels,
    pinnedCols: {
      best: pinnedRelevant && state.showBestCol,
      avg: pinnedRelevant && state.showAvgCol,
      total: pinnedRelevant && state.showTotalCol,
    },
    table_2d_builder_misc: {
      metrics: effectiveMetrics.map((m) => ({
        queryId: m.queryId,
        metricId: m.metricId,
        metricAgg: m.metricAgg,
      })),
      countSpecs: Object.fromEntries(
        effectiveMetrics.filter((m) => m.countSpec).map((m) => [m.queryId, m.countSpec!]),
      ),
      dateRange: state.dateRange,
      showAdvancedDate: state.showAdvancedDate,
      startDateType: state.startDateType,
      startDateLatestN: state.startDateLatestN,
      startDateAbsolute: state.startDateAbsolute,
      startDateOffset: state.startDateOffset,
      endDateType: state.endDateType,
      endDateAbsolute: state.endDateAbsolute,
      endDateOffset: state.endDateOffset,
      dayFilter: state.dayFilter,
      dateControllerId: state.dateControllerId,
      subjectControllerId: state.subjectControllerId,
      subjectControllerMode: state.subjectControllerMode,
      selectedSubjects: state.selectedSubjects,
      selectedDrills: state.selectedDrills,
      subjectActiveTab: state.subjectActiveTab,
      selectedSubjectNames,
      excludeInjuredPlayers: state.excludeInjuredPlayers,
    },
  };
}
